/**
 * Formatação e leitura de número — com o locale **DECLARADO**, não o do navegador.
 *
 * ## Por que este arquivo existe, em vez de um patch no upstream (F1 / decisão D-A)
 *
 * O card oferecia duas rotas para BRL: **(a)** acrescentar `currencyBRL` ao
 * `NumberFormatSchema` do upstream, ou **(b)** a coluna nova formatar como moeda
 * configurável. Recomendava (b) por não conflitar com o fork (Regra 8.15).
 *
 * O Discovery achou o segundo motivo, e ele é de **correção**:
 *
 * ```ts
 * // data-view/src/property-presets/number/utils/formatter.ts:47
 * const formatter = new Intl.NumberFormat(navigator.language, formatterOptions);
 * ```
 *
 * O locale vem do **navegador**. Um `currencyBRL` acrescentado ao enum deles entregaria o
 * símbolo `R$` com os separadores de quem está olhando — `R$ 1,234.56` num navegador `en-US`.
 * Para uma empresa brasileira isso não é detalhe: é o valor errado escrito de um jeito que
 * parece certo, que é o formato de defeito que este repositório mais cataloga.
 *
 * O mesmo vale na ENTRADA: `getLocaleDecimalSeparator()` também consulta `navigator.language`,
 * então `1.234,56` digitado num navegador `en-US` seria lido como `1234.56`... ou como `1.234`,
 * dependendo do caminho. Aqui a leitura é declarada e determinística.
 *
 * ⚠️ **O que este arquivo NÃO faz:** ele não substitui o `number` do upstream nem mexe nele.
 * A coluna `number` deles continua exatamente como está. Isto vale para a coluna de fórmula
 * (e para o que mais nós escrevermos), e é o que mantém a cópia pura.
 */

/** Os formatos de saída que a coluna de fórmula oferece (T2.6 do card). */
export type FormatoDeSaida =
  | 'numero'
  | 'moeda'        // R$ — a razão de ser deste arquivo
  | 'percentual'
  | 'data'
  | 'texto'
  | 'booleano';

export const FORMATOS: readonly FormatoDeSaida[] =
  ['numero', 'moeda', 'percentual', 'data', 'texto', 'booleano'] as const;

/** O locale de toda instalação nossa. Parametrizável — o teste precisa fixar. */
export const LOCALE_PADRAO = 'pt-BR';
export const MOEDA_PADRAO = 'BRL';

export interface OpcoesDeFormato {
  locale?: string;
  moeda?: string;
  /** `undefined` = 2 casas para moeda e percentual, até 4 para número. */
  casas?: number;
}

/**
 * Número → texto.
 *
 * ⚠️ `Intl.NumberFormat` com `style: 'percent'` **multiplica por 100**. Numa fórmula
 * financeira `prop("Realizado") / prop("Orçado")` devolve `0.87`, e o usuário espera ler
 * `87%` — então a multiplicação é o comportamento certo, e está dito aqui porque a alternativa
 * (formatar `0.87` como `0,87%`) é o erro que se comete uma vez e não se percebe.
 */
export function formatar(
  valor: number,
  formato: FormatoDeSaida,
  localeOuOpcoes: string | OpcoesDeFormato = LOCALE_PADRAO
): string {
  const opcoes: OpcoesDeFormato = typeof localeOuOpcoes === 'string'
    ? { locale: localeOuOpcoes }
    : localeOuOpcoes;
  const locale = opcoes.locale || LOCALE_PADRAO;
  const moeda = opcoes.moeda || MOEDA_PADRAO;

  if (!Number.isFinite(valor)) return '';

  switch (formato) {
    case 'moeda':
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: moeda,
        currencyDisplay: 'symbol',
        minimumFractionDigits: opcoes.casas ?? 2,
        maximumFractionDigits: opcoes.casas ?? 2,
      }).format(valor);

    case 'percentual':
      return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: opcoes.casas ?? 1,
        maximumFractionDigits: opcoes.casas ?? 1,
      }).format(valor);

    case 'data': {
      // Epoch ms, lido em UTC — mesma razão de `funcoes.ts`: o mesmo documento aberto de
      // fusos diferentes precisa mostrar a mesma data.
      const d = new Date(valor);
      if (!Number.isFinite(d.getTime())) return '';
      return new Intl.DateTimeFormat(locale, {
        timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric',
      }).format(d);
    }

    case 'booleano':
      return valor ? 'sim' : 'não';

    case 'texto':
      return String(valor);

    case 'numero':
    default:
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: opcoes.casas ?? 0,
        maximumFractionDigits: opcoes.casas ?? 4,
      }).format(valor);
  }
}

/**
 * Texto → número, no locale DECLARADO (T1.3 do card).
 *
 * `1.234,56` em pt-BR é mil duzentos e trinta e quatro vírgula cinquenta e seis. `1,234.56` em
 * en-US é o mesmo número. Sem declarar o locale, `1.234` é ambíguo entre **1234** e **1,234** —
 * e o `parseNumber` do upstream resolve essa ambiguidade consultando o navegador, que é
 * exatamente o que não queremos.
 *
 * ⚠️ Devolve `null` para o que não é número — **nunca `NaN`**. `NaN` atravessa aritmética sem
 * lançar e contamina toda conta a jusante em silêncio; `null` obriga quem chama a decidir.
 */
export function lerNumero(entrada: string, locale: string = LOCALE_PADRAO): number | null {
  const texto = String(entrada ?? '').trim();
  if (!texto) return null;

  const separadores = separadoresDe(locale);
  let limpo = texto
    .replace(/[\s  ]/g, '')     // espaço comum, NBSP e o espaço estreito do Intl
    .replace(/[^\d,.\-+]/g, '');          // fora símbolo de moeda, "%", letras

  if (separadores.milhar && limpo.includes(separadores.milhar)) {
    // ⚠️ **O agrupamento tem FORMA, e ela é conferida antes de ser removida.**
    //
    // Tirar todos os separadores de milhar sem olhar transforma `1.2.3` em `123` — um número
    // que ninguém digitou, a partir de uma entrada que não é número nenhum. Foi o que este
    // código fazia, e o teste pegou.
    //
    // Em pt-BR o milhar separa grupos de exatamente 3 dígitos depois do primeiro grupo (que
    // tem 1 a 3). `1.2.3` não é isso; `1.234.567` é. Conferir a forma é o que separa
    // *"entrada válida com separador"* de *"entrada malformada que eu vou adivinhar"*, e a
    // regra 8.9 diz qual das duas devolver.
    const parteInteira = limpo.split(separadores.decimal)[0].replace(/^[-+]/, '');
    const sep = separadores.milhar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!new RegExp(`^\\d{1,3}(${sep}\\d{3})+$`).test(parteInteira)) return null;
    limpo = limpo.split(separadores.milhar).join('');
  }
  if (separadores.decimal !== '.') {
    limpo = limpo.replace(separadores.decimal, '.');
  }

  // Sobrou mais de um ponto? Não é número — é entrada malformada, e adivinhar qual ponto é o
  // decimal seria o "default que finge" da regra 8.9.
  if ((limpo.match(/\./g) || []).length > 1) return null;

  // ⚠️ Sobrou dígito nenhum? Então NÃO era número, e `Number('')` é **0**.
  //
  // Este era o defeito: `lerNumero("abc")` limpava tudo e devolvia `0` — um zero que ninguém
  // digitou, indistinguível de um zero que alguém digitou. Num campo de valor, é o lançamento
  // de R$ 0,00 que entra na soma. Pego pelo teste "o que não é número devolve null, NUNCA NaN",
  // que existia para vigiar o `NaN` e acabou pegando o irmão dele.
  if (!/\d/.test(limpo)) return null;

  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
}

/** Quais separadores o locale usa — perguntado ao `Intl`, não escrito à mão. */
function separadoresDe(locale: string): { decimal: string; milhar: string | null } {
  const partes = new Intl.NumberFormat(locale, { useGrouping: true })
    .formatToParts(1234567.8);
  const decimal = partes.find(p => p.type === 'decimal')?.value ?? '.';
  const milhar = partes.find(p => p.type === 'group')?.value ?? null;
  return { decimal, milhar };
}
